import type {DataRouterFileOverviewPayload, FileOverviewPayload, OverviewPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RecentlyTouchedBranches} from './RecentlyTouchedBranches'
import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {InterractionLimitsBanner} from './banners/InterractionLimitsBanner'
import PendingInvitationBanner from './banners/PendingInvitationBanner'
import {ProtectBranchBanner} from './banners/ProtectBranchBanner'
import {UseActionBanner} from './banners/UseActionBanner'
import {ImmutableActionsMigrationBanner} from './banners/ImmutableActionsMigrationBanner'
import {DeployBanner} from './banners/DeployBanner'
import {TransactionalMessageBanner} from '@github-ui/in-product-targeting'
import PublishBanners from '@github-ui/code-view-shared/components/PublishBanners'
import {
  SpoofedCommitWarningBanner,
  useLatestCommitIsSpoofed,
} from '@github-ui/code-view-shared/components/SpoofedCommitWarning'

import styles from './OverviewHeader.module.css'
import {useCurrentUser} from '@github-ui/current-user'
import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'
import {useLatestCommitContext} from '@github-ui/code-view-shared/contexts/LatestCommitContext'

export function OverviewHeader({payload}: {payload: FileOverviewPayload | DataRouterFileOverviewPayload}) {
  const repo = useCurrentRepository()
  const user = useCurrentUser()
  const isDataRouter = useIsDataRouterEnabled()

  return (
    <div className={styles.Box}>
      {user && <RecentlyTouchedBranches repo={repo} />}
      {isDataRouter ? (
        <OverviewBanners overview={payload.overview} />
      ) : (
        <OverviewBannersWithFetch overview={payload.overview} />
      )}
    </div>
  )
}

function OverviewBanners({overview}: {overview: OverviewPayload}) {
  const {latestCommit} = useLatestCommitContext()
  const isSpoofed = latestCommit?.isSpoofed ?? false

  return <OverviewBannersInternal overview={overview} isSpoofed={isSpoofed} />
}

function OverviewBannersWithFetch({overview}: {overview: OverviewPayload}) {
  const isSpoofed = useLatestCommitIsSpoofed()

  return <OverviewBannersInternal overview={overview} isSpoofed={isSpoofed} />
}

function OverviewBannersInternal({overview, isSpoofed}: {overview: OverviewPayload; isSpoofed: boolean}) {
  const {
    showUseActionBanner,
    showProtectBranchBanner,
    actionId,
    actionSlug,
    publishBannersInfo: {dismissActionNoticePath, releasePath, showPublishActionBanner},
    interactionLimitBanner,
    showInvitationBanner,
    inviterName,
    actionsMigrationBannerInfo,
    showDeployBanner,
    detectedStack,
    transactionalMessageBanner,
  } = overview.banners
  const {helpUrl} = useReposAppPayload()

  return (
    <>
      {isFeatureEnabled('ipm_global_transactional_message_repos') && transactionalMessageBanner ? (
        <TransactionalMessageBanner banner={transactionalMessageBanner} />
      ) : null}
      {showDeployBanner && <DeployBanner detectedStack={detectedStack} />}
      {isSpoofed && <SpoofedCommitWarningBanner />}
      {interactionLimitBanner && <InterractionLimitsBanner interactionLimitBanner={interactionLimitBanner} />}
      {showInvitationBanner && inviterName && <PendingInvitationBanner inviterName={inviterName} />}
      <PublishBanners
        showPublishActionBanner={showPublishActionBanner}
        releasePath={releasePath}
        dismissActionNoticePath={dismissActionNoticePath}
        className={styles.PublishBanners}
      />
      {showUseActionBanner && actionSlug && actionId ? (
        <UseActionBanner actionSlug={actionSlug} actionId={actionId} />
      ) : null}
      {showProtectBranchBanner && <ProtectBranchBanner helpUrl={helpUrl} />}
      {actionsMigrationBannerInfo.showImmutableActionsMigrationBanner && (
        <ImmutableActionsMigrationBanner
          releaseTags={actionsMigrationBannerInfo.releaseTags}
          initialMigrationStatus={actionsMigrationBannerInfo.initialMigrationStatus}
        />
      )}
    </>
  )
}
